import { Check, ListChecks, Loader2, NotebookPen, Pause } from 'lucide-react';
import { Badge } from '../ui/Badge';
import type { ActividadEstado, ActividadResumen } from '../../lib/sesion';

/**
 * Itinerario de la sesión — el hilo del método en vivo.
 *
 * ≥1024px: stepper VERTICAL. Los números son información (la agenda es una
 * secuencia real del método) y la línea que conecta los pasos se va LLENANDO
 * conforme se congelan actas: el progreso del método de un vistazo.
 * <1024px: barra horizontal compacta de puntos numerados scrolleables, con el
 * paso en vivo resaltado y el detalle del paso elegido debajo.
 *
 * El diario de campo (asíncronas) es un paso APARTE al final, con su propio
 * lenguaje (NotebookPen): no se mezcla en la secuencia.
 */

/** Copy canónico del dominio del acta — nada de jerga técnica. */
const estadoTexto: Record<ActividadEstado, string> = {
  cerrada: 'Acta congelada',
  abierta: 'En vivo',
  pausada: 'En pausa',
  preparada: 'Preparada',
};

const estadoColor: Record<ActividadEstado, string> = {
  cerrada: 'text-success',
  abierta: 'text-primary',
  pausada: 'text-warning',
  preparada: 'text-content-muted',
};

/** Punto pulsante sutil del paso en vivo (respeta prefers-reduced-motion). */
function PulsoVivo() {
  return (
    <span
      className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary animate-pulse motion-reduce:animate-none"
      aria-hidden="true"
    />
  );
}

/**
 * Nodo del paso: lenguaje visual inequívoco por estado.
 * cerrada = check · en vivo = número con borde primary · preparada = punto
 * hueco con su número · pausada = pausa en warning · diario = NotebookPen.
 */
function NodoPaso({
  estado,
  numero,
  diario = false,
}: {
  estado: ActividadEstado;
  numero?: number;
  diario?: boolean;
}) {
  const base =
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border bg-surface-base font-display text-base font-semibold tabular-nums';
  if (diario) {
    return (
      <span className={`${base} border-info text-info`}>
        <NotebookPen className="h-4 w-4" aria-hidden="true" />
      </span>
    );
  }
  if (estado === 'cerrada') {
    return (
      <span className={`${base} border-success text-success`}>
        <Check className="h-4 w-4" aria-hidden="true" />
      </span>
    );
  }
  if (estado === 'abierta') {
    return <span className={`${base} border-2 border-primary text-primary`}>{numero}</span>;
  }
  if (estado === 'pausada') {
    return (
      <span className={`${base} border-warning text-warning`}>
        <Pause className="h-4 w-4" aria-hidden="true" />
      </span>
    );
  }
  return <span className={`${base} border-border-strong text-content-muted`}>{numero}</span>;
}

/**
 * Marcar/quitar del diario de campo. Mismo gate de conducción que abrir/cerrar;
 * la mutación vive en la página, acá solo el botón (target ≥44px).
 */
function BotonDiario({
  esDiario,
  marcando,
  onClick,
}: {
  esDiario: boolean;
  marcando: boolean;
  onClick: () => void;
}) {
  const etiqueta = esDiario ? 'Quitar del diario de campo' : 'Marcar como diario de campo';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={marcando}
      aria-label={etiqueta}
      title={`${etiqueta} — recibe aportes entre sesiones mientras su fase siga activa`}
      className="flex min-h-touch min-w-touch shrink-0 items-center justify-center rounded-md text-content-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-content focus-visible:outline-none focus-visible:shadow-focus disabled:pointer-events-none disabled:opacity-50"
    >
      {marcando ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <NotebookPen className={`h-4 w-4 ${esDiario ? 'text-info' : ''}`} aria-hidden="true" />
      )}
    </button>
  );
}

/** Meta pequeña bajo el nombre: modo de trabajo + duración prevista. */
function metaPaso(item: ActividadResumen): string {
  const partes: string[] = [];
  if (item.modo) partes.push(item.modo);
  if (item.duracion_min) partes.push(`${item.duracion_min} min`);
  return partes.join(' · ');
}

/** Tarjeta del paso (compartida por la secuencia y el diario, escritorio). */
function TarjetaPaso({
  item,
  activa,
  puedeConducir,
  marcando,
  onSelect,
  onToggleDiario,
}: {
  item: ActividadResumen;
  activa: boolean;
  puedeConducir: boolean;
  marcando: boolean;
  onSelect: () => void;
  onToggleDiario: () => void;
}) {
  const enVivo = item.estado === 'abierta';
  const meta = metaPaso(item);
  return (
    <div
      className={`flex min-w-0 flex-1 items-start gap-1 rounded-md border pr-1 transition-colors duration-fast ${
        enVivo
          ? 'border-primary bg-surface-raised'
          : activa
            ? 'border-border-strong bg-surface-raised'
            : 'border-transparent hover:bg-surface-raised'
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-current={activa ? 'true' : undefined}
        className="flex min-h-touch min-w-0 flex-1 flex-col justify-center rounded-md px-3 py-2 text-left focus-visible:outline-none focus-visible:shadow-focus"
      >
        <span
          className={`block truncate font-sans text-sm text-content ${
            activa || enVivo ? 'font-semibold' : ''
          }`}
        >
          {item.herramienta_nombre}
        </span>
        {meta && (
          <span className="block truncate font-sans text-xs text-content-muted">{meta}</span>
        )}
        <span
          className={`flex items-center gap-1.5 font-sans text-xs ${estadoColor[item.estado]}`}
        >
          {enVivo && <PulsoVivo />}
          {estadoTexto[item.estado]}
        </span>
      </button>
      {puedeConducir && item.estado !== 'cerrada' && (
        <BotonDiario
          esDiario={Boolean(item.asincrona)}
          marcando={marcando}
          onClick={onToggleDiario}
        />
      )}
    </div>
  );
}

/** Punto numerado de la barra horizontal (<1024px). Target ≥44px. */
function PuntoPaso({
  item,
  numero,
  activa,
  diario = false,
  onSelect,
}: {
  item: ActividadResumen;
  numero?: number;
  activa: boolean;
  diario?: boolean;
  onSelect: () => void;
}) {
  const enVivo = item.estado === 'abierta';
  const etiqueta = `${diario ? 'Diario de campo: ' : `Paso ${numero}: `}${
    item.herramienta_nombre
  } — ${estadoTexto[item.estado]}`;
  return (
    <li className="shrink-0">
      <button
        type="button"
        onClick={onSelect}
        aria-current={activa ? 'true' : undefined}
        aria-label={etiqueta}
        title={etiqueta}
        className={`relative flex min-h-touch min-w-touch items-center justify-center rounded-full focus-visible:outline-none focus-visible:shadow-focus ${
          activa ? 'bg-surface-raised' : ''
        }`}
      >
        <NodoPaso estado={item.estado} numero={numero} diario={diario} />
        {enVivo && (
          <span
            className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary animate-pulse motion-reduce:animate-none"
            aria-hidden="true"
          />
        )}
      </button>
    </li>
  );
}

export function Itinerario({
  actividades,
  actividadId,
  cargando,
  puedeConducir,
  marcandoId,
  onSelect,
  onToggleDiario,
}: {
  actividades: ActividadResumen[];
  /** Actividad enfocada en la Cabina (seleccionada o foco por defecto). */
  actividadId: string | null;
  cargando: boolean;
  /** Mismo permiso de conducción que abrir/cerrar la actividad. */
  puedeConducir: boolean;
  /** Id de la actividad cuyo marcado de diario está en vuelo. */
  marcandoId: string | null;
  onSelect: (id: string) => void;
  onToggleDiario: (item: ActividadResumen) => void;
}) {
  const secuencia = actividades.filter((a) => !a.asincrona);
  const diario = actividades.filter((a) => a.asincrona);
  const seleccionado = actividades.find((a) => a.id === actividadId) ?? null;

  return (
    <nav aria-label="Itinerario de la sesión" className="flex flex-col gap-3">
      <h2 className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-content-muted">
        <ListChecks className="h-4 w-4" aria-hidden="true" />
        Itinerario de la sesión
      </h2>

      {cargando ? (
        <p className="font-sans text-sm text-content-muted">Cargando el itinerario…</p>
      ) : actividades.length === 0 ? (
        <p className="font-sans text-sm text-content-muted">
          Esta sesión todavía no tiene actividades preparadas.
        </p>
      ) : (
        <>
          {/* ---- Móvil: barra compacta de puntos numerados scrolleables ---- */}
          <div className="lg:hidden">
            <ol className="flex items-center gap-1 overflow-x-auto pb-1">
              {secuencia.map((item, i) => (
                <PuntoPaso
                  key={item.id}
                  item={item}
                  numero={i + 1}
                  activa={item.id === actividadId}
                  onSelect={() => onSelect(item.id)}
                />
              ))}
              {diario.length > 0 && (
                <li aria-hidden="true" className="mx-1 h-6 w-px shrink-0 bg-border" />
              )}
              {diario.map((item) => (
                <PuntoPaso
                  key={item.id}
                  item={item}
                  diario
                  activa={item.id === actividadId}
                  onSelect={() => onSelect(item.id)}
                />
              ))}
            </ol>

            {/* Detalle del paso elegido: nombre, meta, estado y diario. */}
            {seleccionado && (
              <div className="mt-1 flex items-center gap-1 rounded-md border border-border bg-surface-raised pl-3 pr-1">
                <div className="min-w-0 flex-1 py-2">
                  <p className="truncate font-sans text-sm font-semibold text-content">
                    {seleccionado.herramienta_nombre}
                  </p>
                  <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-sans text-xs text-content-muted">
                    {metaPaso(seleccionado) && <span>{metaPaso(seleccionado)}</span>}
                    <span
                      className={`flex items-center gap-1.5 ${estadoColor[seleccionado.estado]}`}
                    >
                      {seleccionado.estado === 'abierta' && <PulsoVivo />}
                      {estadoTexto[seleccionado.estado]}
                    </span>
                    {seleccionado.asincrona && <Badge variant="info">Diario de campo</Badge>}
                  </p>
                </div>
                {puedeConducir && seleccionado.estado !== 'cerrada' && (
                  <BotonDiario
                    esDiario={Boolean(seleccionado.asincrona)}
                    marcando={marcandoId === seleccionado.id}
                    onClick={() => onToggleDiario(seleccionado)}
                  />
                )}
              </div>
            )}
          </div>

          {/* ---- Escritorio: stepper vertical con línea de progreso ---- */}
          <ol className="hidden lg:flex lg:flex-col">
            {secuencia.map((item, i) => (
              <li key={item.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <NodoPaso estado={item.estado} numero={i + 1} />
                  {i < secuencia.length - 1 && (
                    /* LA FIRMA: la línea se llena cuando el acta congeló. */
                    <div
                      aria-hidden="true"
                      className={`min-h-4 w-0.5 flex-1 rounded-full ${
                        item.estado === 'cerrada' ? 'bg-success' : 'bg-border'
                      }`}
                    />
                  )}
                </div>
                <div className="mb-3 flex min-w-0 flex-1">
                  <TarjetaPaso
                    item={item}
                    activa={item.id === actividadId}
                    puedeConducir={puedeConducir}
                    marcando={marcandoId === item.id}
                    onSelect={() => onSelect(item.id)}
                    onToggleDiario={() => onToggleDiario(item)}
                  />
                </div>
              </li>
            ))}
          </ol>

          {diario.length > 0 && (
            <div className="hidden lg:block">
              <h3 className="flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-wide text-content-muted">
                <NotebookPen className="h-3.5 w-3.5" aria-hidden="true" />
                Diario de campo
              </h3>
              <p className="mt-0.5 font-sans text-xs text-content-muted">
                Recibe aportes entre sesiones mientras su fase siga activa.
              </p>
              <ol className="mt-2 flex flex-col gap-1">
                {diario.map((item) => (
                  <li key={item.id} className="flex gap-3">
                    <NodoPaso estado={item.estado} diario />
                    <TarjetaPaso
                      item={item}
                      activa={item.id === actividadId}
                      puedeConducir={puedeConducir}
                      marcando={marcandoId === item.id}
                      onSelect={() => onSelect(item.id)}
                      onToggleDiario={() => onToggleDiario(item)}
                    />
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
    </nav>
  );
}
